import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sparkles, ArrowRight, Home, Briefcase, ShieldCheck, Wallet, MessageSquare, Star, CheckCircle2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { homePath } from "@/lib/api";
import { useEffect } from "react";

const HERO = "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjB3YXJtJTIwbW9kZXJuJTIwbGl2aW5nJTIwcm9vbSUyMGludGVyaW9yfGVufDB8fHx8MTc4OTM2MzkyMHww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Briefcase, title: "Job Pipeline", desc: "Track every clean from request to payout with a live Kanban board." },
  { icon: Home, title: "Property Management", desc: "Store instructions, checklists and photos for every listing." },
  { icon: MessageSquare, title: "Real-time Chat", desc: "Message cleaners per job with instant WebSocket notifications." },
  { icon: Wallet, title: "Automatic Payouts", desc: "Cleaners get paid via Stripe Connect the moment you approve." },
  { icon: ShieldCheck, title: "Verified Cleaners", desc: "Background checks and verification keep your properties safe." },
  { icon: Star, title: "Reviews & Ratings", desc: "Build trust with a transparent review system on every job." },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (user && user.role) navigate(homePath(user.role));
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-white">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-ocean flex items-center justify-center rounded-md">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-extrabold text-xl tracking-tight text-sapphire">Clean Slate Connect</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login">Log In</Button>
            <Button className="bg-ocean hover:bg-ocean/90" onClick={() => navigate("/register")} data-testid="nav-signup">
              Get Started
            </Button>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-ocean/10 text-ocean text-xs font-semibold uppercase tracking-[0.2em] mb-6">
            <Sparkles className="w-3 h-3" /> Turnover cleaning, automated
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-sapphire leading-[1.05]">
            The operating system for <span className="text-ocean">turnover cleaning</span>.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-xl">
            Schedule cleans, dispatch to trusted pros or the marketplace, chat in real time, and pay
            automatically — all in one tactical dashboard built for hosts and cleaners.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" className="bg-ocean hover:bg-ocean/90 hard-shadow-sm hover:-translate-y-0.5 active:translate-y-0 transition-transform"
              onClick={() => navigate("/register?role=pm")} data-testid="cta-host">
              I'm a Host <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button size="lg" variant="outline" className="border-sapphire text-sapphire hover:bg-sapphire hover:text-white transition-colors"
              onClick={() => navigate("/register?role=cleaner")} data-testid="cta-cleaner">
              I'm a Cleaner
            </Button>
          </div>
          <div className="mt-8 flex items-center gap-6 text-sm text-muted-foreground">
            {["Background-checked pros", "Instant payouts", "No monthly fees"].map((t) => (
              <span key={t} className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" />{t}</span>
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="absolute -inset-3 bg-ocean/10 rounded-lg -rotate-2" />
          <img src={HERO} alt="Clean apartment" className="relative rounded-lg border-2 border-sapphire hard-shadow w-full object-cover aspect-[4/3]" />
        </div>
      </section>

      <section className="bg-white border-y border-border py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-heading font-extrabold text-3xl tracking-tight text-sapphire mb-2">Everything you need to run turnovers</h2>
          <p className="text-muted-foreground mb-10">From the first request to the final payout.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f) => (
              <div key={f.title} className="border border-border rounded-md p-6 bg-white hover:border-ocean transition-colors" data-testid="feature-card">
                <div className="w-11 h-11 rounded-md bg-ocean/10 flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5 text-ocean" />
                </div>
                <h3 className="font-heading font-bold text-lg text-sapphire mb-1">{f.title}</h3>
                <p className="text-sm text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16 text-center">
        <h2 className="font-heading font-black text-3xl lg:text-4xl tracking-tight text-sapphire">Ready for a spotless turnover?</h2>
        <p className="text-muted-foreground mt-3 mb-6">Join hosts and cleaners already running on Clean Slate Connect.</p>
        <Button size="lg" className="bg-ocean hover:bg-ocean/90" onClick={() => navigate("/register")} data-testid="cta-bottom">
          Create your free account <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </section>

      <footer className="border-t border-border bg-white py-8">
        <div className="max-w-7xl mx-auto px-6 text-sm text-muted-foreground flex justify-between">
          <span data-testid="footer-copyright">© 2026 Clean Slate Connect. Owned and operated by Clean Slate Biohazard Response Team LLC.</span>
          <span>Built for turnover cleaning teams</span>
        </div>
      </footer>
    </div>
  );
}
